import ReactModal from "react-modal";
import styled from "styled-components";
import {
  SpaceProps,
  LayoutProps,
  ColorProps,
  TypographyProps,
  FlexboxProps,
  PositionProps,
} from "styled-system";
import { H2 } from "../Heading";
import { LinebreakParagraph } from "../Paragraph";
import { Flex } from "../Flex";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { IoMdClose } from "react-icons/io";
import { Image } from "../Image";
import success from "../../assets/success-icon.svg";
import error from "../../assets/error-icon.svg";
import { useState } from "react";
import bannerImage from "../../assets/modal-banner.svg";
import { Box } from "../Box";
import { Label } from "../Label";
import { stripHtmlTags } from "../../utils/string";

type ModalProps = SpaceProps &
  ColorProps &
  TypographyProps &
  LayoutProps &
  FlexboxProps &
  PositionProps & {
    /** Must be a styled-system color key. */
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    hasModalBannerImage?: boolean;
    content?: string;
    onModalConfirmClick?: any;
    onSecondaryButtonClick?: any;
    secondaryButtonText?: string;
    primaryButtonText?: string;
    primaryButtonVariant?: "danger" | "primary";
    secondaryButtonVariant?: "danger" | "primary" | "text" | "danger-text";
    hasSecondaryButton?: boolean;
    hasSecondaryActionButton?: boolean;
    hasPrimaryButton?: boolean;
    isSuccessful?: boolean;
    isClosingDisabled?: boolean;
    isLoadingAction?: boolean;
    hasIcon?: boolean;
    hasCloseIcon?: boolean;
    children?: any;
    width?: string;
    loadingActionText?: string;
    doubleClick?: boolean;
    hasCommentBox?: boolean;
  };

const ModalStyle = styled.div<any>`
  margin: 2rem;
  padding: 20px;
  border-radius: 12px;
  top: "50%";
  left: "50%";
  right: "auto";
  bottom: "auto";
  marginright: "-50%";
  transform: "translate(-50%, -50%)";
  overflow: unset;

  ${({ theme: { breakpoints }, width, height }) => `

    ${width ? `width: ${width};` : ""}
    ${height ? `height: ${height};` : ""}

    @media screen and (max-width: ${breakpoints.md}) {
      width: 80%;
      height: 50vh;
    }
  `};
`;

const CommentTextArea = styled.textarea<any>`
  ${({ theme: { radii, colors, space, fontSizes } }: any) => `
      resize: none;
      width: 100%;
      box-sizing: border-box;
      border-radius: ${radii[0]};
      border: 1px solid ${colors.gray[30]};
      font-family: "Inter", sans-serif;
      color: ${colors.blue[100]};
      font-size: ${fontSizes[1]};
      padding: ${space[2]};
      transition: border-color .15s ease, box-shadow .15s ease;

      ::placeholder {
          color: ${colors.gray[50]};
      }

        :hover {
            border: 1px solid ${colors.gray[40]};
        }

        :focus, :focus-visible {
            outline: none;
            border-color: ${colors.blue.primary};
            box-shadow: 0 0 0 3px ${colors.blue[50]};
        }
    `}
`;

const OverlayStyle = styled.div`
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3500;
  background: red;
  overflow-y: auto;
`;

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "12px",
    border: "none",
    boxShadow: "0 12px 32px rgba(9, 9, 62, 0.12)",
    padding: "30px",
    maxHeight: "95%",
  },
  overlay: {
    backgroundColor: "rgba(9, 9, 62, 0.45)",
  },
};

export const Modal = (props: ModalProps) => {
  const {
    hasModalBannerImage,
    isOpen,
    onClose,
    title,
    content,
    onModalConfirmClick,
    onSecondaryButtonClick,
    secondaryButtonText,
    primaryButtonText,
    primaryButtonVariant,
    secondaryButtonVariant,
    hasSecondaryButton,
    hasSecondaryActionButton,
    hasPrimaryButton = true,
    isClosingDisabled,
    isLoadingAction,
    loadingActionText,
    isSuccessful,
    hasIcon,
    hasCloseIcon = true,
    children,
    width,
    height,
    doubleClick,
    hasCommentBox = false,
  } = props;
  const [value, setValue] = useState("");
  const [isCommentBoxHasValue, setIsCommentBoxHasValue] = useState(false);

  const resetComment = () => {
    setValue("");
    setIsCommentBoxHasValue(false);
  };

  const handleChange = (event) => {
    const sanitizedValue = stripHtmlTags(event.target.value);
    setValue(sanitizedValue);
    if (sanitizedValue.length > 2) {
      setIsCommentBoxHasValue(true);
    } else {
      setIsCommentBoxHasValue(false);
    }
  };

  return (
    <ReactModal
      shouldCloseOnOverlayClick={!isClosingDisabled}
      isOpen={isOpen}
      onRequestClose={onClose}
      appElement={document.getElementById("root") || undefined}
      contentElement={(props, children) => (
        <ModalStyle {...props} width={width} height={height}>
          {children}
        </ModalStyle>
      )}
      overlayElement={(props, contentElement) => (
        <OverlayStyle {...props}>{contentElement}</OverlayStyle>
      )}
      style={customStyles}
    >
      {hasCloseIcon && (
        <IconButton
          isDisabled={isClosingDisabled}
          onClick={
            isClosingDisabled
              ? () => {}
              : () => {
                  resetComment();
                  onClose();
                }
          }
          Type={IoMdClose}
          position="absolute"
          right={2}
          size="23px"
          top={2}
        />
      )}
      {hasModalBannerImage && (
        <Box
          backgroundColor={"#D0D7E2"}
          p={2}
          borderRadius={10}
          textAlign={"center"}
          mb={2}
        >
          <Image
            src={bannerImage}
            alt="feature banner"
            // height="100%"
            // width="100%"
          />
        </Box>
      )}
      {children ? (
        children
      ) : (
        <>
          {hasIcon && (
            <Image
              src={isSuccessful ? success : error}
              alt="indicator icon"
              height="48px"
              width="48px"
            />
          )}
          <H2 mr={3}>{title}</H2>
          <LinebreakParagraph fontSize="1" color="gray.60">
            {content}
          </LinebreakParagraph>

          {hasCommentBox && (
            <>
              <Label>Comment</Label>
              <CommentTextArea
                name="textarea"
                value={value}
                onChange={handleChange}
                placeholder="Enter comment"
              ></CommentTextArea>
            </>
          )}
          <Flex justifyContent="end">
            {hasSecondaryButton && (
              <Button
                type="button"
                isDisabled={isClosingDisabled}
                // onClick={onClose}
                onClick={() => {
                  resetComment();
                  onClose();
                }}
                variant={
                  secondaryButtonVariant ? secondaryButtonVariant : "text"
                }
                my="3"
                mr={2}
                // color="gray.50"
                color={
                  secondaryButtonVariant === "danger-text"
                    ? "red.primary"
                    : "gray.50"
                }
              >
                {secondaryButtonText}
              </Button>
            )}
            {hasSecondaryActionButton && (
              <Button
                type="button"
                isDisabled={isClosingDisabled}
                onClick={() => {
                  onSecondaryButtonClick(value);
                }}
                variant={
                  secondaryButtonVariant ? secondaryButtonVariant : "text"
                }
                my="3"
                mr={2}
                color={
                  secondaryButtonVariant === "danger-text"
                    ? "red.primary"
                    : "gray.50"
                }
              >
                {secondaryButtonText}
              </Button>
            )}
            {hasPrimaryButton && (
              <Button
                type="button"
                isDisabled={
                  isClosingDisabled ||
                  doubleClick ||
                  (hasCommentBox && !isCommentBoxHasValue)
                }
                onClick={() => {
                  resetComment();
                  onModalConfirmClick(value);
                }}
                variant={primaryButtonVariant}
                my="3"
              >
                {isLoadingAction ? loadingActionText : primaryButtonText}
              </Button>
            )}
          </Flex>
        </>
      )}
    </ReactModal>
  );
};
